import { useNavigate } from 'react-router-dom';
import React, { useState, useEffect } from 'react';
import { FaTrash, FaEdit } from 'react-icons/fa';

import '../../src/createAdmin.css';
import {
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    TextField,
    IconButton,
    InputAdornment,
    Tooltip,
    CircularProgress
} from "@mui/material";
import { Button } from '@mui/material';
import * as XLSX from 'xlsx';
import { Visibility, VisibilityOff } from "@mui/icons-material";
import { toast } from "react-toastify";
import { trainerCreateApi, fetchTrainerDetails, deleteTrainerDetails, updateTrainerDetails } from "../api/createTrainer";
import { getApiErrorMessage } from "../utils/apiError";

// Utility function to format dates from ISO to DD/MM/YY HH:MM:SS
const formatDate = (isoDate) => {
    if (!isoDate) return '';
    try {
        const date = new Date(isoDate);
        if (isNaN(date.getTime())) return isoDate;
        
        const day = String(date.getDate()).padStart(2, '0');
        const month = String(date.getMonth() + 1).padStart(2, '0');
        const year = String(date.getFullYear()).slice(-2);
        const hours = String(date.getHours()).padStart(2, '0');
        const minutes = String(date.getMinutes()).padStart(2, '0');
        const seconds = String(date.getSeconds()).padStart(2, '0');
        
        return `${day}/${month}/${year} ${hours}:${minutes}:${seconds}`;
    } catch (error) {
        return isoDate;
    }
};

// Utility function to truncate text with ellipsis
const truncateText = (text, maxLength = 30) => {
    if (!text) return text;
    if (text.length <= maxLength) return text;
    return text.substring(0, maxLength) + '...';
};

const CreateTrainer = () => {

    const [TrainerDetails, setTrainer] = useState([]);
    const [currentPage, setCurrentPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    // starts true so the first paint is the loader, never an empty table
    const [loading, setLoading] = useState(true);

    const [showPassword, setShowPassword] = useState(false);
    const [password, setPassword] = useState("");

    const [open, setOpen] = useState(false);
    const [add, setAdd] = useState(false);

    const [Name, setName] = useState("");
    const [email, setEmail] = useState("")
    const [phoneNumber, setPhoneNumber] = useState("")
    const [trainerId, setTrainerID] = useState("")

    const [formErrors, setFormErrors] = useState({
        name: "",
        email: "",
        phoneNumber: "",
        password: ""
    });
    const [submitting, setSubmitting] = useState(false);
    // snapshot of the row as it was opened — used to detect edits
    const [initialForm, setInitialForm] = useState(null);

    const navigate = useNavigate();

    const loggedInEmail = (localStorage.getItem('emailId') || '').trim().toLowerCase();

    const TRAINER_NAME_MAX_LENGTH = 150;

    // Top-right, auto dismiss, solid green/red background
    const TOAST_OPTIONS = {
        position: "top-right",
        autoClose: 3000,
        theme: "colored",
    };

    const clearErrors = () => {
        setFormErrors({
            name: "",
            email: "",
            phoneNumber: "",
            password: ""
        });
    };

    // pure — builds the error map without touching state, so the submit
    // button can ask "is this form valid?" on every render
    const buildErrors = (isAdd) => {
        const errors = {
            name: "",
            email: "",
            phoneNumber: "",
            password: ""
        };

        const trimmedName = Name.trim();
        const trimmedEmail = email.trim();
        const trimmedPhone = phoneNumber.trim();

        if (!trimmedName) {
            errors.name = "Name is required.";
        } else if (Name.length > TRAINER_NAME_MAX_LENGTH) {
            errors.name = `Trainer name must be at most ${TRAINER_NAME_MAX_LENGTH} characters.`;
        }

        if (!trimmedEmail) {
            errors.email = "Email is required.";
        } else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmedEmail)) {
            errors.email = "Please enter a valid email address.";
        }

        if (!trimmedPhone) {
            errors.phoneNumber = "Phone number is required.";
        } else if (!/^\d+$/.test(trimmedPhone)) {
            errors.phoneNumber = "Phone number must contain only digits.";
        } else if (!/^[6-9]/.test(trimmedPhone)) {
            errors.phoneNumber = "Phone number must start with 6, 7, 8 or 9.";
        } else if (trimmedPhone.length !== 10) {
            errors.phoneNumber = "Phone number must be exactly 10 digits.";
        }

        if (isAdd) {
            if (!password.trim()) {
                errors.password = "Password is required.";
            } else if (password.trim().length < 6) {
                errors.password = "Password must be at least 6 characters.";
            }
        }

        return errors;
    };

    const validateFields = (isAdd) => {
        const errors = buildErrors(isAdd);
        setFormErrors(errors);
        return Object.values(errors).every((value) => value === "");
    };

    const isFormValid = Object.values(buildErrors(add)).every((value) => value === "");

    // Save stays off until something actually changed — compared trimmed,
    // since trailing spaces are not an edit
    const isFormDirty = !initialForm
        ? false
        : Name.trim() !== (initialForm.name || "").trim() ||
          email.trim() !== (initialForm.email || "").trim() ||
          phoneNumber.trim() !== (initialForm.phoneNumber || "").trim();

    const TABLE_COLUMNS = 8;
    const PAGE_SIZE = 10;
    // keeps the table the same height as a full 10-row page while empty/loading
    const ROW_HEIGHT = 56;
    const EMPTY_TABLE_HEIGHT = PAGE_SIZE * ROW_HEIGHT;

    const getTrainer = async (page = 1) => {
        setLoading(true);
        try {
            const data = await fetchTrainerDetails(page, PAGE_SIZE);
            console.log(data, "data")

            setTrainer(Array.isArray(data?.data?.data) ? data.data.data : []);
            setTotalPages(data?.data?.totalPages || 1);
            setCurrentPage(data?.data?.currentPage || page);
        } catch (error) {
            console.error('Error fetching trainers:', error);
            // fall through to the No Data state instead of stale rows
            setTrainer([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        console.log(currentPage, "currentPage")
        getTrainer(currentPage);
    }, [currentPage]);



    const handleClickShowPassword = () => {
        setShowPassword((prev) => !prev);
    };

    const handlePageChange = (newPage) => {
        if (newPage >= 1 && newPage <= totalPages) {
            console.log(newPage, "'newPage")
            setCurrentPage(newPage);
        }
    };

    const handleBackClick = () => {
        navigate('/dashboard');
    };

    const convertToCSV = (data) => {
        const header = ["ID", "Trainer Name", "Trainer Email", "Trainer Phone Number", 'Created At'];
        const rows = data.map(trainer => [
            trainer.id,
            trainer.name,
            trainer.email,
            trainer.phoneNumber,
            formatDate(trainer.createdAt)
        ]);

        const csvContent = [
            header.join(","),
            ...rows.map(row => row.join(","))
        ].join("\n");

        return csvContent;
    };

    const downloadCSV = () => {
        const csvContent = convertToCSV(TrainerDetails);

        const blob = new Blob([csvContent], { type: "text/csv;charset=utf-8;" });

        const link = document.createElement("a");
        const url = URL.createObjectURL(blob);

        link.setAttribute("href", url);
        link.setAttribute("download", "trainer_data.csv");
        link.style.visibility = "hidden";
        document.body.appendChild(link);
        link.click();
        document.body.removeChild(link);
    };

    const downloadExcel = () => {
        const header = ["ID", "Trainer Name", "Trainer Email", "Trainer Phone Number", 'Created At'];
        const rows = TrainerDetails.map(trainer => ({
            "ID": trainer.id,
            "Trainer Name": trainer.name,
            "Trainer Email": trainer.email,
            "Trainer Phone Number": trainer.phoneNumber,
            "Created At": formatDate(trainer.createdAt)
        }));

        const ws = XLSX.utils.json_to_sheet(rows, { header });
        const wb = XLSX.utils.book_new();
        XLSX.utils.book_append_sheet(wb, ws, "Trainer Data");

        XLSX.writeFile(wb, "trainer_data.xlsx");
    };


    const handleDeleteTrainer = async (data) => {
        if (!window.confirm(`Are you sure you want to delete trainer "${data.name}"?`)) {
            return;
        }
        
        try {
            const result = await deleteTrainerDetails(data.id);
            if (result) {
                setTrainer((prev) => prev.filter((trainer) => trainer.id !== data.id));
                alert("Trainer deleted successfully");
            }

        } catch (err) {
            console.error('Error deleting trainer', err);
            const errorMessage = err.response?.data?.msg || err.response?.data?.message || "Failed to delete trainer, please try again.";
            alert(errorMessage);
        }

    }





    const handleClickOpen = (data, check) => {
        setOpen(true);
        clearErrors();
        if (check === "add") {
            setName("");
            setEmail("");
            setPhoneNumber("");
            setTrainerID("");
            setInitialForm(null);
            setAdd(true);
        } else {
            setName(data.name || "");
            setEmail(data.email || "");
            setPhoneNumber(data.phoneNumber || "");
            setTrainerID(data.id || "");
            setInitialForm({
                name: data.name || "",
                email: data.email || "",
                phoneNumber: data.phoneNumber || "",
            });
            setAdd(false);
        }
    };

    const handleClose = () => {
        setOpen(false);
        setName("");
        setEmail("");
        setPhoneNumber("")
        setTrainerID("")
        setAdd(false)
        setPassword("")
        setInitialForm(null);
        clearErrors();
    };

    const addTrainer = async () => {
        // guards against a second call slipping in before the re-render
        if (submitting) {
            return;
        }
        if (!validateFields(true)) {
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                name: Name.trim(),
                email: email.trim(),
                phoneNumber: phoneNumber.trim(),
                password: password
            };

            console.log("Payload:", payload);
            const response = await trainerCreateApi(payload)
            // trainerCreateApi returns the raw axios response
            const body = response?.data;

            if (body?.success === false) {
                toast.error(body?.msg || body?.message || "Failed to create trainer, please try again.", TOAST_OPTIONS);
                return;
            }

            if (response) {
                toast.success(body?.msg || body?.message || "Trainer created successfully.", TOAST_OPTIONS);
                handleClose();
                getTrainer(currentPage);
            }

        } catch (err) {
            console.error("Add trainer failed:", err);
            // Show the actual error message from the backend
            toast.error(getApiErrorMessage(err, "Failed to create trainer, please try again."), TOAST_OPTIONS);
        } finally {
            // re-enabled only once the response (or failure) is in
            setSubmitting(false);
        }
    };



    const editTrainer = async () => {
        if (submitting) {
            return;
        }
        if (!validateFields(false)) {
            return;
        }

        setSubmitting(true);
        try {
            const payload = {
                name: Name.trim(),
                email: email.trim(),
                phoneNumber: phoneNumber.trim(),
                trainerId: trainerId
            };

            const response = await updateTrainerDetails(payload)
            // updateTrainerDetails returns the raw axios response
            const body = response?.data;

            if (body?.success === false) {
                toast.error(body?.msg || body?.message || "Failed to update trainer, please try again.", TOAST_OPTIONS);
                return;
            }

            if (response) {
                toast.success(body?.msg || body?.message || "Trainer updated successfully.", TOAST_OPTIONS);
                handleClose();
                getTrainer(currentPage);
            }
        } catch (err) {
            console.error("Edit trainer failed:", err);
            toast.error(getApiErrorMessage(err, "Failed to update trainer, please try again."), TOAST_OPTIONS);
        } finally {
            setSubmitting(false);
        }
    }


    return (
        <div>
            <h1>Trainers</h1>
            {/* Back on the left, downloads grouped with Add Trainer on the right */}
            <div
                style={{
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'space-between',
                    gap: '10px',
                    flexWrap: 'wrap',
                    marginBottom: '16px',
                }}
            >
                <Button
                    variant="contained"
                    color="secondary"
                    onClick={handleBackClick}
                >
                    Back to Dashboard
                </Button>

                <div style={{ display: 'flex', alignItems: 'center', gap: '10px', flexWrap: 'wrap' }}>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={downloadCSV}
                    >
                        Download CSV
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={downloadExcel}
                    >
                        Download Excel
                    </Button>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={() => handleClickOpen("", "add")}
                    >
                        Add Trainer
                    </Button>
                </div>
            </div>

            <table style={{ width: '100%', borderCollapse: 'collapse', marginTop: '20px' }}>
                <thead>
                    <tr>
                        <th>S.No.</th>
                        <th style={{ textAlign: 'left' }}>Trainer Name</th>
                        <th style={{ textAlign: 'left' }}>Trainer Email</th>
                        <th>Trainer Phone Number</th>
                        <th>Referral Code</th>
                        <th>Created At</th>
                        <th>Updated At</th>
                        <th>Action</th>
                    </tr>
                </thead>
                <tbody>
                    {loading ? (
                        // Loader stays until the response lands
                        <tr>
                            <td
                                colSpan={TABLE_COLUMNS}
                                style={{
                                    height: EMPTY_TABLE_HEIGHT,
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                }}
                            >
                                <CircularProgress size={40} />
                            </td>
                        </tr>
                    ) : TrainerDetails.length > 0 ? (
                        TrainerDetails.map((trainer, index) => (
                            <tr key={index}>
                                {/* serial number continues across pages: page 2 starts at 11 */}
                                <td>{(currentPage - 1) * PAGE_SIZE + index + 1}</td>
                                <td style={{ textAlign: 'left' }}>
                                    <Tooltip title={trainer.name} arrow>
                                        <span>{truncateText(trainer.name, 30)}</span>
                                    </Tooltip>
                                </td>
                                <td style={{ textAlign: 'left' }}>
                                    <Tooltip title={trainer.email} arrow>
                                        <span>{truncateText(trainer.email, 30)}</span>
                                    </Tooltip>
                                </td>
                                <td>{trainer.phoneNumber}</td>
                                <td>{trainer.AdminReferralCode}</td>

                                <td>{formatDate(trainer.createdAt)}</td>
                                <td>{formatDate(trainer.updatedAt)}</td>
                                <td>
                                    <button
                                        onClick={() => handleClickOpen(trainer, "edit")}
                                        style={{
                                            background: 'blue',
                                            color: 'white',
                                            marginRight: '10px',
                                            border: 'none',
                                            padding: '6px 10px',
                                            borderRadius: '5px',
                                            cursor: 'pointer'
                                        }}
                                    >
                                        <FaEdit />
                                    </button>

                                    {!(loggedInEmail && (trainer.email || '').trim().toLowerCase() === loggedInEmail) && (
                                        <button
                                            onClick={() => handleDeleteTrainer(trainer)}
                                            style={{
                                                background: 'red',
                                                color: 'white',
                                                border: 'none',
                                                padding: '6px 10px',
                                                borderRadius: '5px',
                                                cursor: 'pointer'
                                            }}
                                        >
                                            <FaTrash />
                                        </button>
                                    )}
                                </td>
                            </tr>
                        ))
                    ) : (
                        <tr>
                            <td
                                colSpan={TABLE_COLUMNS}
                                style={{
                                    height: EMPTY_TABLE_HEIGHT,
                                    textAlign: "center",
                                    verticalAlign: "middle",
                                    fontWeight: 500,
                                    color: "#666",
                                }}
                            >
                                No Data Available
                            </td>
                        </tr>
                    )}
                </tbody>
            </table>

            {/* hidden while loading and when there is nothing to page through */}
            {!loading && TrainerDetails.length > 0 && (
                <div>
                    <button onClick={() => handlePageChange(currentPage - 1)} disabled={loading || currentPage === 1}>
                        Previous
                    </button>
                    <span> Page {currentPage} of {totalPages} </span>
                    <button onClick={() => handlePageChange(currentPage + 1)} disabled={loading || currentPage === totalPages}>
                        Next
                    </button>
                </div>
            )}

            <div>
                <Dialog open={open} onClose={handleClose} fullWidth maxWidth="sm">
                    <DialogTitle sx={{ fontWeight: 'bold' }}>
                        {add ? 'Add Trainer' : 'Edit Trainer'}
                    </DialogTitle>
                    <DialogContent>
                        <TextField
                            label="Name*"
                            type="text"
                            fullWidth
                            value={Name}
                            onChange={(e) => {
                                const value = e.target.value.substring(0, TRAINER_NAME_MAX_LENGTH);
                                setName(value);
                                setFormErrors((prev) => ({ ...prev, name: "" }));
                            }}
                            inputProps={{ maxLength: TRAINER_NAME_MAX_LENGTH }}
                            error={Boolean(formErrors.name)}
                            helperText={formErrors.name || `${Name.length}/${TRAINER_NAME_MAX_LENGTH} characters`}
                            sx={{ mt: 2 }}
                        />
                        <TextField
                            label="Email*"
                            type="email"
                            fullWidth
                            value={email}
                            onChange={(e) => {
                                setEmail(e.target.value);
                                setFormErrors((prev) => ({ ...prev, email: "" }));
                            }}
                            error={Boolean(formErrors.email)}
                            helperText={formErrors.email}
                            sx={{ mt: 2 }}
                        />
                        <TextField
                            label="Phone Number*"
                            type="text"
                            fullWidth
                            value={phoneNumber}
                            onChange={(e) => {
                                // digits only, hard-capped at 10 — no browser spinner since this is a text field
                                const value = e.target.value.replace(/\D/g, "").slice(0, 10);
                                setPhoneNumber(value);
                                setFormErrors((prev) => ({
                                    ...prev,
                                    phoneNumber: value && !/^[6-9]/.test(value)
                                        ? "Phone number must start with 6, 7, 8 or 9."
                                        : ""
                                }));
                            }}
                            inputProps={{ inputMode: "numeric", maxLength: 10 }}
                            error={Boolean(formErrors.phoneNumber)}
                            helperText={formErrors.phoneNumber || `${phoneNumber.length}/10 digits`}
                            sx={{ mt: 2 }}
                        />
                        {add && (
                            <TextField
                                label="Password*"
                                type={showPassword ? "text" : "password"}
                                fullWidth
                                value={password}
                                onChange={(e) => {
                                    setPassword(e.target.value);
                                    setFormErrors((prev) => ({ ...prev, password: "" }));
                                }}
                                sx={{ mt: 2 }}
                                InputProps={{
                                    endAdornment: (
                                        <InputAdornment position="end">
                                            <IconButton onClick={handleClickShowPassword} edge="end">
                                                {showPassword ? <VisibilityOff /> : <Visibility />}
                                            </IconButton>
                                        </InputAdornment>
                                    ),
                                }}
                                error={Boolean(formErrors.password)}
                                helperText={formErrors.password}
                            />
                        )}
                    </DialogContent>
                    {/* px matches DialogContent so the buttons line up with the fields */}
                    <DialogActions sx={{ px: 3, pb: 3 }}>
                        <Button onClick={handleClose} color="secondary" disabled={submitting}>
                            Cancel
                        </Button>
                        {add ? (
                            <Button
                                onClick={addTrainer}
                                color="primary"
                                variant="contained"
                                disabled={!isFormValid || submitting}
                                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {submitting ? "Adding..." : "Add"}
                            </Button>
                        ) : (
                            <Button
                                onClick={editTrainer}
                                color="primary"
                                variant="contained"
                                disabled={!isFormValid || !isFormDirty || submitting}
                                startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
                            >
                                {submitting ? "Saving..." : "Save"}
                            </Button>
                        )}
                    </DialogActions>
                </Dialog>
            </div>



        </div>
    );

}


export default CreateTrainer;

